import { supabase } from '../lib/supabase';

export const checklistService = {
  // NSherjFh PR 8: default-filters soft-deleted checklist templates.
  // Pass `{ includeDeleted: true }` from the checklist-management
  // 'View deleted' tab. Mirrors PR 2-7 opt-in shape.
  //
  // Template-vs-completion decision documented in the PR summary +
  // migration header: ACTIVE reads (this method, getChecklistById,
  // getAllChecklistTemplates) filter the template's deleted_at.
  // HISTORICAL reads (getChecklistCompletions, getCompletionsFor
  // Report) intentionally do NOT — a worker's signed completion is
  // a fact regardless of whether the parent template is now
  // deleted. Same pattern as rams_acknowledgements (PR 3) and
  // snag_comments (PR 4).
  //
  // is_template (boolean column on site_checklists) is a separate
  // axis from deleted_at — templates and instances can both be
  // soft-deleted; soft-delete is orthogonal to template-vs-instance.
  async getChecklistsBySite(siteId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('site_checklists')
        ?.select('*, checklist_items(*)')
        ?.eq('site_id', siteId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getChecklistById(checklistId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('site_checklists')
        ?.select('*, checklist_items(*)')
        ?.eq('id', checklistId);
      if (!includeDeleted) query = query?.is('deleted_at', null);
      // .maybeSingle so a soft-deleted id resolves to null gracefully
      // (the ChecklistCompletion executor renders not-found) instead
      // of throwing PGRST116.
      const { data, error } = await query?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getChecklistCompletions(siteId, date) {
    try {
      const { data, error } = await supabase?.from('checklist_completions')?.select('*, site_checklists(checklist_name), user_profiles!checklist_completions_completed_by_fkey(full_name), checklist_item_responses(*)')?.eq('site_id', siteId)?.eq('completion_date', date)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async createChecklistCompletion(completionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('checklist_completions')?.insert({
          checklist_id: completionData?.checklistId,
          site_id: completionData?.siteId,
          completion_date: completionData?.completionDate,
          completed_by: user?.id,
          signature_data: completionData?.signatureData || null,
          notes: completionData?.notes || null
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async createChecklistItemResponse(responseData) {
    try {
      const { data, error } = await supabase?.from('checklist_item_responses')?.insert({
          completion_id: responseData?.completionId,
          checklist_item_id: responseData?.checklistItemId,
          status: responseData?.status,
          comment: responseData?.comment || null,
          photo_path: responseData?.photoPath || null,
          captured_at: responseData?.capturedAt || null,
          latitude: responseData?.latitude || null,
          longitude: responseData?.longitude || null
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async uploadChecklistPhoto(file, companyId, siteId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `checklists/${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${siteId}/${fileName}`;

      const { error: uploadError } = await supabase?.storage?.from('construction-documents')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;
      return { filePath, error: null };
    } catch (error) {
      return { filePath: null, error: { message: error?.message } };
    }
  },

  async createChecklistTemplate(templateData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('site_checklists')?.insert({
          site_id: templateData?.siteId,
          company_id: templateData?.companyId,
          checklist_name: templateData?.checklistName,
          description: templateData?.description,
          is_template: true,
          created_by: user?.id
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async createChecklistItem(itemData) {
    try {
      const { data, error } = await supabase?.from('checklist_items')?.insert({
          checklist_id: itemData?.checklistId,
          category: itemData?.category,
          item_description: itemData?.itemDescription,
          is_mandatory: itemData?.isMandatory ?? true,
          display_order: itemData?.displayOrder ?? 0
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateChecklistTemplate(checklistId, updates) {
    try {
      const { data, error } = await supabase?.from('site_checklists')?.update(updates)?.eq('id', checklistId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // NSherjFh PR 8 — recoverable soft-delete on the checklist template.
  //
  // Previously a hard DELETE called by checklist-management's
  // window.confirm flow. Now an idempotent UPDATE stamping deleted_at
  // + deleted_by. checklist_items rows + checklist_completions +
  // checklist_item_responses remain intact in the DB — completions
  // are historical facts (worker X completed template Y on date Z),
  // visible in historical-class reads (getChecklistCompletions,
  // getCompletionsForReport) regardless of template's deleted_at.
  //
  // RLS: covered by the existing admins_manage_checklists FOR ALL
  // policy (USING company_id + role IN admin/SM/worker; no deleted_at
  // predicate, so restore round-trip works). UI gates Delete /
  // Restore / View Deleted to admin only via isAdmin even though
  // RLS permits SM/worker — same documented pattern as PRs 4-6-7.
  async deleteChecklistTemplate(checklistId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('site_checklists')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', checklistId)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Inverse of deleteChecklistTemplate. Existing completions were
  // never touched, so restore is a single UPDATE on the template
  // row — the active-view filters unblock and the template surfaces
  // in checklist-management, getAllChecklistTemplates, worker
  // daily-site-checklist, etc.
  async restoreChecklistTemplate(checklistId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('site_checklists')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', checklistId)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getAllChecklistTemplates(options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('site_checklists')
        ?.select('*, checklist_items(*)')
        ?.eq('is_template', true)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getCompletionsForReport(siteId, companyId, dateFrom, dateTo) {
    try {
      let query = supabase
        ?.from('checklist_completions')
        ?.select('id, completion_date, notes, site_checklists(checklist_name), user_profiles!checklist_completions_completed_by_fkey(full_name), checklist_item_responses(id, status, photo_path)');

      if (siteId) query = query?.eq('site_id', siteId);
      if (companyId) query = query?.eq('company_id', companyId);
      if (dateFrom) query = query?.gte('completion_date', dateFrom);
      if (dateTo) query = query?.lte('completion_date', dateTo);

      query = query?.order('completion_date', { ascending: false });

      const { data, error } = await query;
      if (error) throw error;

      const completions = (data || [])?.map(c => {
        const responses = c?.checklist_item_responses || [];
        const total = responses?.length;
        const passed = responses?.filter(r => r?.status === 'pass')?.length;
        const failed = responses?.filter(r => r?.status === 'fail')?.length;
        const na = responses?.filter(r => r?.status === 'na' || r?.status === 'n/a')?.length;
        const answered = passed + failed;
        const completion_pct = total > 0 ? Math.round((answered / total) * 100) : 0;
        const has_photos = responses?.some(r => !!r?.photo_path);

        return {
          id: c?.id,
          checklist_name: c?.site_checklists?.checklist_name || '—',
          completed_at: c?.completion_date,
          inspector_name: c?.user_profiles?.full_name || '—',
          total_items: total,
          passed,
          failed,
          na,
          completion_pct,
          notes: c?.notes || '',
          has_photos,
        };
      });

      return { data: completions, error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },
};